import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { EmptyState, FlexBox, Modal, ModalBody, ModalFoot, ModalHead, Spinner, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { ConversationEventGroups, ConversationSummaryByCsid, } from 'components/conversation';
import { TagChips } from 'components/tags/tagChips';
import { useReadPatchedConversation } from 'utils/conversation/useReadPatchedConversation';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import styles from './conversationPreviewModal.module.scss';
/** The config for which event groups to present in the preview. */
const groupsConfig = [
    'group:pstn_phone',
    'group:voicemail',
    {
        'group:contact_form': { hideAttachments: true, hideGutterActions: true },
        'group:email': { hideAttachments: true, hideGutterActions: true },
        'group:messenger': { hideAttachments: true, hideGutterActions: true },
        'group:widget_chat': { hideAttachments: true, hideGutterActions: true },
        'group:facebook_messenger': {
            hideAttachments: true,
            hideGutterActions: true,
        },
        'group:whats_app': { hideAttachments: true, hideGutterActions: true },
        'group:sms': { hideAttachments: true, hideGutterActions: true },
        'group:twitter': { hideAttachments: true, hideGutterActions: true },
        'group:instagram_story': { hideGutterActions: true },
        'group:conversation:wrapup_message': { hideGutterActions: true },
    },
];
/** Represents the modal for previewing a conversation. */
export const ConversationPreviewModal = createAppModal({ type: 'app:conversationPreview' }, ({ isOpen, modal, hide }) => {
    const navigate = useAppNavigate();
    const [isFulfilled, setIsFulfilled] = useState(false);
    const [readConversation, { data: conversationData, asyncStatus }] = useReadPatchedConversation();
    const mainConversation = conversationData === null || conversationData === void 0 ? void 0 : conversationData.data.history.main_conversation;
    /** Handles closing the modal. */
    const handleClose = useCallback(() => {
        setIsFulfilled(false);
        hide === null || hide === void 0 ? void 0 : hide();
    }, [hide]);
    /** Handles navigating to the currently previewed conversation. */
    const handleViewConversation = useCallback(() => {
        navigate({
            path: '/conversation/:csid',
            params: { path: { csid: modal.arg.csid.toString() } },
        });
        handleClose();
    }, [handleClose, modal, navigate]);
    // Handles fetching the conversation.
    useEffect(() => {
        if (isOpen) {
            readConversation({ csid: modal.arg.csid });
        }
    }, [isOpen, modal.arg.csid, readConversation]);
    // Handles delaying fulfilled state to prevent elements being switched too
    // quickly.
    useDebounce(() => {
        if (isOpen) {
            setIsFulfilled(asyncStatus === 'fulfilled');
        }
    }, 300, [asyncStatus, isOpen]);
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, className: styles.root, onClose: handleClose, allowClickOutside: true }, { children: [_jsx(ModalHead, { title: `Conversation #${modal.arg.csid} preview`, onClose: handleClose }), _jsx(ModalBody, { children: isFulfilled && mainConversation ? (_jsxs(_Fragment, { children: [mainConversation.tags.length > 0 && (_jsx(FlexBox, Object.assign({ margin: "0 0 300" }, { children: _jsx(TagChips, { tags: mainConversation.tags }) }))), mainConversation.summary.can_be_generated && (_jsx(ConversationSummaryByCsid, { csid: mainConversation.id, initialSummary: mainConversation.summary.current, view: "preview", className: styles.summary })), _jsx(ConversationEventGroups, { mainConversation: mainConversation, groupsConfig: groupsConfig, emptyMessage: _jsx(EmptyState, { title: "Nothing to preview", description: "There are no messages to preview in the conversation." }) })] })) : (_jsx(FlexBox, Object.assign({ justifyContent: "center", alignItems: "center", height: "100px" }, { children: _jsx(Spinner, { size: 32 }) }))) }), _jsx(ModalFoot, { cancelName: "Close", cancelOnClick: handleClose, confirmName: "View conversation", confirmOnClick: handleViewConversation })] })));
});
